import { AvatarNew, Button } from '@platform/components';
import { OrganizationProfileResponse } from '@platform/client/schemas';
import { DirectoryInnerSection } from '../../directory/ContentSection';
import { DirectoryOuterSection } from '../../directory/DirectoryContentGrid';
import { SubheaderPanel } from '../../directory/SubheaderPanel';
import { TwoColumnInnerContent } from '../../directory/TwoColumnInnerContent';
import { VolunteerRolePanel } from '../../directory/projects/VolunteerRolePanel';
import { OrgDescriptions } from '../../directory/OrgDescriptions';
import { OrgPillsPanel } from '../../directory/organizations/OrgPillsPanel';
import { OrgInfoCard } from '../../directory/OrgInfoCard';
import { RepsList } from '../../directory/RepsList';
import { DescriptionBulletList } from '../../directory/DescriptionBulletList';
import { PillsList } from '../../directory/PillsList';

interface ProjectSuggestionPreviewProps {
  title: string;
  description: string;
  deliverables: string[];
  skills: string[];
  learningOutcomes: string;
  orgData: OrganizationProfileResponse;
  onClose: () => void;
}

export const ProjectSuggestionPreview = ({
  title,
  description,
  deliverables,
  skills,
  learningOutcomes,
  orgData,
  onClose,
}: ProjectSuggestionPreviewProps) => {
  return (
    <div className='fixed inset-bs-18 inset-0 z-25 bg-white overflow-y-auto'>
      <div className='bg-secondary-brand-25 px-4 sm:px-0'>
        <div className='max-w-[900px] sm:px-10 pt-8'>
          <Button onClick={onClose} variant='link-color' size='sm' leadIcon='arrow-left'>
            Back to your project
          </Button>
        </div>
        <div className='mx-auto sm:max-w-[1216px] sm:px-4 py-8'>
          <div className='text-4xl font-semibold text-gray-900'>{title}</div>
        </div>
      </div>

      <DirectoryOuterSection noTopMargin>
        <DirectoryInnerSection title='About'>
          <TwoColumnInnerContent>
            <div className='flex flex-col gap-6'>
              {description.length > 0 && (
                <SubheaderPanel title='Description'>
                  <p className='text-md text-gray-500'>{description}</p>
                </SubheaderPanel>
              )}
              {deliverables.length > 0 && (
                <SubheaderPanel title='Possible deliverables'>
                  <DescriptionBulletList items={deliverables} />
                </SubheaderPanel>
              )}
              {learningOutcomes.length > 0 && (
                <SubheaderPanel title="What you'll learn">
                  <p className='text-md text-gray-500'>{learningOutcomes}</p>
                </SubheaderPanel>
              )}
              <SubheaderPanel title='Preferred skills'>
                <PillsList items={skills} />
              </SubheaderPanel>
            </div>
            <VolunteerRolePanel roleType={"consultant"} />
          </TwoColumnInnerContent>
        </DirectoryInnerSection>

        <DirectoryInnerSection title='Organization'>
          <TwoColumnInnerContent>
            <div className='flex flex-col gap-5'>
              <div className='flex flex-col sm:flex-row items-center gap-2 sm:gap-0 text-center sm:text-left'>
                <div className='w-14 h-14 sm:mr-3'>
                  <AvatarNew
                    url={orgData.avatarUrl}
                    withPadding
                    fallbackIcon='building-04'
                    alt='Organization Logo'
                  />
                </div>
                <div className='flex flex-col'>
                  <span className='text-base font-semibold text-gray-900'>{orgData.orgName}</span>
                  <span className='text-sm text-gray-500'>
                    {orgData.locationCity}, {orgData.locationCountry}
                  </span>
                </div>
                <div className='hidden sm:block ml-auto'>
                  <Button variant='secondary-gray' size='md' tailIcon='arrow-narrow-up-right' isDisabled>
                    See full profile
                  </Button>
                </div>
              </div>
              <OrgDescriptions orgData={orgData} maxLength={550} />
            </div>
            <OrgPillsPanel
              sustainabilityGoals={orgData.sustainabilityGoals}
              industries={orgData.industries}
            />
          </TwoColumnInnerContent>
          <OrgInfoCard isLoaded orgData={orgData} />
        </DirectoryInnerSection>

        <DirectoryInnerSection title='Representatives'>
          <RepsList reps={orgData.reps} />
        </DirectoryInnerSection>
      </DirectoryOuterSection>
    </div>
  );
};
